import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as styles from '~src/styles/domains/adverse/AdversePage.module.scss';
import { trackPageMount } from '~src/utils/tracking';
import DocumentLink from '../common/components/DocumentLink';
import { Page } from '../common/enums/api';
import { Doc } from '../common/enums/docs';
import { setOfferExpired } from '../common/state/commonReducer';
import {
  brandSelector,
  commonDocsSelector,
  isOnboardSpendSelector,
  previousPageSelector,
} from '../common/state/commonSelectors';
import { SessionType } from '../common/types/api';

const AdversePage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isOnboardSpend = useSelector(isOnboardSpendSelector);
  const brand = useSelector(brandSelector);
  const previousPage = useSelector(previousPageSelector);

  const commonDocs = useSelector(
    commonDocsSelector,
  ) as SessionType['common_docs'];

  const isFromSSNFailure = previousPage === Page.FULL_SSN;

  useEffect(() => {
    batch(() => {
      dispatch(trackPageMount(Page.ADVERSE));
      dispatch(setOfferExpired());
    });
  }, []);

  const adverseActionDoc = commonDocs?.[Doc.AA];
  const adverseActionLink = adverseActionDoc && (
    <DocumentLink
      id={Doc.AA}
      url={adverseActionDoc.url}
      text={t('adverse:click-here')}
    />
  );

  const getHeaderKey = () => {
    if (isFromSSNFailure) return 'header-ssn-failure';
    if (isOnboardSpend) return 'header-onboard-spend';
    return 'header';
  };

  return (
    <div className={styles['adverse-wrapper']}>
      <div className={`header-txt ${styles['adverse-header']}`}>
        <Trans t={t} i18nKey={`adverse:${getHeaderKey()}`} />
      </div>
      <div className={styles['adverse-body']}>
        {!isOnboardSpend && !isFromSSNFailure && (
          <Trans t={t} i18nKey={'adverse:cannot-proceed'} />
        )}
        {isFromSSNFailure && (
          <Trans t={t} i18nKey={'adverse:cannot-proceed-ssn-failure'} />
        )}
        {adverseActionLink && !isFromSSNFailure && (
          <Trans
            t={t}
            i18nKey={`adverse:${brand.toLowerCase()}.additional-information`}
            components={[
              adverseActionLink,
              <a
                href={t(`adverse:${brand.toLowerCase()}.payLink`)}
                target='_blank'
                rel='no-opener'
              />,
            ]}
          />
        )}
      </div>
    </div>
  );
};

export default AdversePage;
